import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { SUBSTRATES } from '../config/products';
import { cn } from '../lib/utils';
import { 
  Check, Printer, Box, Layers, TreeDeciduous, 
  Shield, Square, HelpCircle, Gem, Cuboid, Combine,
  Droplets, Waves, Ban
} from 'lucide-react';

const iconMap = {
  Printer: Printer,
  Box: Box,
  Gem: Gem,
  Layers: Layers,
  TreeDeciduous: TreeDeciduous,
  Shield: Shield,
  Square: Square,
  HelpCircle: HelpCircle,
  Amphora: Box,
  Cuboid: Cuboid,
  Combine: Combine,
};

// Out of stock items
const OUT_OF_STOCK = ['glass'];

const cardVariants = {
  hidden: { opacity: 0, y: 15, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.04,
      duration: 0.35,
      ease: [0.25, 0.1, 0.25, 1]
    }
  })
};

export const SubstrateSelector = ({ selectedSubstrate, onSelect }) => {
  const { isRTL } = useLanguage();

  const substrates = Object.values(SUBSTRATES);

  // Custom icon for mixed substrates
  const getIcon = (substrate) => {
    if (substrate.id === 'mixed') return Combine;
    if (substrate.id === 'ceramic') return Gem;
    return iconMap[substrate.icon] || Box;
  };

  // Primer icon - Droplets for absorbent (Solan), Waves for non-absorbent (Quartz)
  const PrimerIcon = ({ absorbent }) => {
    return absorbent ? (
      <Droplets className="w-3 h-3 text-warning" />
    ) : (
      <Waves className="w-3 h-3 text-accent" />
    );
  };

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
      {substrates.map((substrate, index) => {
        const Icon = getIcon(substrate);
        const isSelected = selectedSubstrate === substrate.id;
        const isOutOfStock = OUT_OF_STOCK.includes(substrate.id);

        return (
          <motion.button
            key={substrate.id}
            custom={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            whileHover={isOutOfStock ? {} : { scale: 1.05, y: -3 }}
            whileTap={isOutOfStock ? {} : { scale: 0.95 }}
            onClick={() => !isOutOfStock && onSelect(substrate.id)}
            data-testid={`substrate-${substrate.id}`}
            disabled={isOutOfStock}
            className={cn(
              'relative p-3 sm:p-4 rounded-2xl border-2 transition-all duration-200 text-center group',
              isOutOfStock
                ? 'border-border/20 bg-secondary/20 cursor-not-allowed opacity-60'
                : isSelected 
                  ? 'border-accent bg-accent/10 shadow-lg shadow-accent/20' 
                  : 'border-border/40 hover:border-accent/40 bg-card hover:bg-secondary/30'
            )}
          >
            {/* Out of Stock Badge */}
            {isOutOfStock && (
              <div className="absolute -top-2 -end-2 z-10">
                <div className="bg-destructive text-destructive-foreground text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-md whitespace-nowrap">
                  {isRTL ? 'אזל' : 'Out'}
                </div>
              </div>
            )}

            {/* Selection indicator */}
            {isSelected && !isOutOfStock && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -end-2 w-6 h-6 rounded-full bg-accent flex items-center justify-center shadow-lg z-10"
              >
                <Check className="w-3.5 h-3.5 text-accent-foreground" />
              </motion.div>
            )}

            <div className="flex flex-col items-center gap-2">
              {/* Icons Row - Main icon + Primer icon */}
              <div className="flex items-center gap-1">
                <div 
                  className={cn(
                    'w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-all',
                    isOutOfStock
                      ? 'bg-secondary/50 text-muted-foreground/50'
                      : isSelected 
                        ? 'bg-accent text-accent-foreground shadow-md' 
                        : 'bg-secondary/80 text-muted-foreground group-hover:text-accent group-hover:bg-secondary'
                  )}
                >
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                
                {/* Small Primer Type Icon */}
                <div 
                  className={cn(
                    'w-6 h-6 rounded-lg flex items-center justify-center',
                    isOutOfStock
                      ? 'bg-secondary/30'
                      : substrate.absorbent 
                        ? 'bg-warning/10' 
                        : 'bg-accent/10'
                  )}
                >
                  <PrimerIcon absorbent={substrate.absorbent} />
                </div>
              </div>
              
              {/* Name only - larger font */}
              <span className={cn(
                'text-sm sm:text-base font-semibold leading-tight',
                isOutOfStock
                  ? 'text-muted-foreground/50'
                  : isSelected ? 'text-accent' : 'text-foreground'
              )}>
                {isRTL ? substrate.nameHe : substrate.nameEn}
              </span>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};
